import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Loader2, X, Building2 } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../core/services/firebase/config';

interface BusinessSuggestion {
  id: string;
  name: string;
  city: string;
  state: string;
  type: string;
}

interface BusinessSearchBarProps {
  onSelect: (business: BusinessSuggestion) => void;
  onClear?: () => void;
  initialValue?: string;
}

export const BusinessSearchBar: React.FC<BusinessSearchBarProps> = ({ 
  onSelect, 
  onClear,
  initialValue = '' 
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<BusinessSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 2) {
        setIsLoading(true);
        try {
          const q = query(
            collection(db, 'tenants'),
            where('businessName', '>=', searchTerm),
            where('businessName', '<=', searchTerm + '\uf8ff'),
            limit(5)
          );
          
          const querySnapshot = await getDocs(q);
          const results: BusinessSuggestion[] = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.businessName,
              city: data.location?.city || '',
              state: data.location?.state || '',
              type: data.tenantType || ''
            };
          });
          
          setSuggestions(results);
          setShowDropdown(true);
        } catch (error) {
          console.error('Error searching businesses:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSelect = (business: BusinessSuggestion) => {
    setSearchTerm(business.name);
    setShowDropdown(false);
    onSelect(business);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowDropdown(false);
    if (onClear) onClear();
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label htmlFor="business-search" className="sr-only">Buscar pousada ou guia</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 size={16} className="text-blue-500 animate-spin" role="status" aria-label="Buscando" />
          ) : (
            <Search size={16} className="text-gray-400" />
          )}
        </div>
        <input
          id="business-search"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
          placeholder="🏢 Check-in (ex: Pousada do Sol)"
          className="w-full pl-10 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50/50 outline-none transition-all"
          aria-label="Buscar pousada ou guia para check-in"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
            aria-label="Limpar busca"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="max-h-60 overflow-y-auto">
            {suggestions.map((business) => (
              <button
                key={business.id}
                type="button"
                onClick={() => handleSelect(business)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-50 last:border-0"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Building2 size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate">{business.name}</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <MapPin size={12} />
                    {business.city}, {business.state}
                  </div>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-500 bg-blue-50 px-2 py-0.5 rounded">
                  {business.type}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
